import React, { useEffect, useMemo, useState } from "react";
import { X, Plus, Trash2, ArrowRight, ArrowLeft, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/leadUtils";

const SCHEDULE_TYPES = [
  { key: "50_50", label: "50 / 50" },
  { key: "30_30_40", label: "30 / 30 / 40" },
  { key: "full", label: "Full Payment" },
  { key: "custom", label: "Custom" },
];

function toISODate(s) {
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function addDays(iso, days) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function computeMacroInstallments(type, total, weddingDate) {
  const t = parseInt(total || 0, 10) || 0;
  const wd = weddingDate || "";
  if (type === "50_50") {
    return [
      { label: "Advance", amount: Math.round(t * 0.5), due_at: todayISO(), due_at_source: "wedding_relative" },
      { label: "Balance", amount: t - Math.round(t * 0.5), due_at: wd, due_at_source: "wedding_relative" },
    ];
  }
  if (type === "30_30_40") {
    const a = Math.round(t * 0.3);
    const m = Math.round(t * 0.3);
    return [
      { label: "Advance", amount: a, due_at: todayISO(), due_at_source: "wedding_relative" },
      { label: "Mid", amount: m, due_at: addDays(wd, -30), due_at_source: "wedding_relative" },
      { label: "Balance", amount: t - a - m, due_at: wd, due_at_source: "wedding_relative" },
    ];
  }
  if (type === "full") {
    return [{ label: "Full payment", amount: t, due_at: wd, due_at_source: "wedding_relative" }];
  }
  return [];
}

export default function OnboardingWizard({ lead, existingProject, eventTypes, serviceTypes, onClose, onComplete }) {
  const isEdit = !!existingProject;

  // Step 1
  const [primaryName, setPrimaryName] = useState(lead?.full_name || "");
  const [partnerName, setPartnerName] = useState(existingProject?.partner_name || "");
  const [phone, setPhone] = useState(lead?.phone_display || lead?.phone || "");
  const [email, setEmail] = useState(lead?.email || "");
  const [secondaryContacts, setSecondaryContacts] = useState(
    (existingProject?.secondary_contacts && existingProject.secondary_contacts.length > 0)
      ? existingProject.secondary_contacts
      : []
  );
  // Step 2
  const initialWedding = toISODate(existingProject?.events?.[0]?.date) || toISODate(lead?.wedding_date) || "";
  const [title, setTitle] = useState(existingProject?.title || `${lead?.full_name || "Client"}'s Wedding`);
  const [weddingDate, setWeddingDate] = useState(initialWedding);
  const [weddingVenue, setWeddingVenue] = useState(
    existingProject?.events?.find((e) => e.type === "Wedding")?.venue || lead?.venue_status || ""
  );
  const [totalQuote, setTotalQuote] = useState(
    existingProject?.total_quote_value || lead?.quote_value || ""
  );
  const [events, setEvents] = useState(() => {
    if (existingProject?.events?.length > 0) {
      return existingProject.events.map((e) => ({
        id: e.id,  // preserve stable event_id for round-trip
        type: e.type, date: toISODate(e.date), venue: e.venue || "",
        services: (e.services || []).map((s) => s.type),
      }));
    }
    return [{ type: "Wedding", date: initialWedding, venue: lead?.venue_status || "", services: [] }];
  });
  // Step 4 (was Step 3 pre-Phase-5-wizard-patch) — Payment schedule
  const [scheduleType, setScheduleType] = useState(existingProject?.payment_schedule_draft?.type || "50_50");
  const [installments, setInstallments] = useState(
    existingProject?.payment_schedule_draft?.installments || computeMacroInstallments("50_50", totalQuote, initialWedding)
  );

  // Step 3 — Phase 5 Wizard Deliverable Quantities patch (2026-06-19).
  // Catalog provides the studio-wide STARTER quantities; salesperson can
  // override each row's quantity for THIS project. Due-days stays studio policy.
  const [catalogDefaults, setCatalogDefaults] = useState({});  // {service_type: [{deliverable_type, quantity, unit, due_days_from_shoot}]}
  const [quantitiesByKey, setQuantitiesByKey] = useState(() => {
    const m = {};
    for (const r of existingProject?.wizard_deliverable_quantities || []) {
      m[`${r.service_type}::${r.deliverable_type}`] = r.quantity ?? "";
    }
    return m;
  });
  const [salesNotes, setSalesNotes] = useState(existingProject?.sales_notes || "");
  useEffect(() => {
    api.get("/catalogs").then((r) => {
      const map = {};
      for (const s of r.data.service_types || []) {
        if (["Candid Photo", "Candid Video", "Traditional Photo", "Traditional Video"].includes(s.name)) {
          map[s.name] = s.default_deliverables || [];
        }
      }
      setCatalogDefaults(map);
    }).catch(() => {});
  }, []);

  // Phase 4.2 — Advance received (always required when wizard fires)
  const firstInstallmentAmt = (installments[0]?.amount || 0);
  const [advanceAmount, setAdvanceAmount] = useState(
    existingProject?.advance_payment?.amount ?? firstInstallmentAmt
  );
  const [advanceMode, setAdvanceMode] = useState(existingProject?.advance_payment?.mode || "upi");
  const [advanceDate, setAdvanceDate] = useState(existingProject?.advance_payment?.received_at || new Date().toISOString().slice(0, 10));
  const [advanceRef, setAdvanceRef] = useState(existingProject?.advance_payment?.reference || "");
  // Re-fill advance to new first installment when schedule changes (only if user hasn't manually edited)
  const [advanceUserEdited, setAdvanceUserEdited] = useState(false);
  useEffect(() => {
    if (advanceUserEdited) return;
    setAdvanceAmount(installments[0]?.amount || 0);
    // eslint-disable-next-line
  }, [scheduleType, installments[0]?.amount]);

  // When schedule type changes (or totalQuote / weddingDate change for macros), recompute
  useEffect(() => {
    if (scheduleType === "custom") return;
    setInstallments(computeMacroInstallments(scheduleType, totalQuote, weddingDate));
    // eslint-disable-next-line
  }, [scheduleType, totalQuote, weddingDate]);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  // Track dirty for cancel confirmation (compared at the field-trigger boundary)
  const initialSnapshotRef = React.useRef(null);
  if (initialSnapshotRef.current === null) {
    initialSnapshotRef.current = JSON.stringify({ primaryName, partnerName, phone, email, secondaryContacts, title, weddingDate, weddingVenue, totalQuote, events, scheduleType, installments });
  }
  const isDirty = () => initialSnapshotRef.current !== JSON.stringify({ primaryName, partnerName, phone, email, secondaryContacts, title, weddingDate, weddingVenue, totalQuote, events, scheduleType, installments });

  const safeClose = () => {
    if (isDirty() && !window.confirm("Cancel and discard changes?")) return;
    onClose?.();
  };

  // ESC + backdrop
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") safeClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
    // eslint-disable-next-line
  }, []);

  const addContact = () => secondaryContacts.length < 5 && setSecondaryContacts([...secondaryContacts, { name: "", relation: "", phone: "", email: "" }]);
  const setContact = (i, patch) => setSecondaryContacts(secondaryContacts.map((c, j) => j === i ? { ...c, ...patch } : c));
  const removeContact = (i) => setSecondaryContacts(secondaryContacts.filter((_, j) => j !== i));

  const addEvent = () => setEvents([...events, { type: "", date: weddingDate, venue: "", services: [] }]);
  const setEvent = (i, patch) => setEvents(events.map((e, j) => j === i ? { ...e, ...patch } : e));
  const removeEvent = (i) => setEvents(events.filter((_, j) => j !== i));
  const toggleEventService = (i, svc) => setEvent(i, { services: events[i].services.includes(svc) ? events[i].services.filter((s) => s !== svc) : [...events[i].services, svc] });

  const setInstallment = (i, patch) => {
    setInstallments(installments.map((inst, j) => {
      if (j !== i) return inst;
      const next = { ...inst, ...patch };
      // Mark date as fixed if user changed it
      if (patch.due_at !== undefined && patch.due_at !== inst.due_at) next.due_at_source = "fixed";
      return next;
    }));
  };
  const addInstallment = () => setInstallments([...installments, { label: "", amount: 0, due_at: weddingDate, due_at_source: "fixed" }]);
  const removeInstallment = (i) => setInstallments(installments.filter((_, j) => j !== i));

  const installmentsSum = installments.reduce((a, b) => a + (parseInt(b.amount || 0, 10) || 0), 0);
  const sumValid = installmentsSum === (parseInt(totalQuote || 0, 10) || 0);

  // Per-step validation
  const step1Valid = primaryName.trim() && phone.trim();
  const step2Valid = weddingDate && (parseInt(totalQuote || 0, 10) > 0);
  // Step 3 (Deliverables) — always valid; quantity overrides are optional.
  const step3Valid = true;
  const advanceAmt = parseInt(advanceAmount || 0, 10) || 0;
  const firstInstAmt = parseInt(installments[0]?.amount || 0, 10) || 0;
  const advanceValid = advanceAmt > 0 && (firstInstAmt === 0 || advanceAmt <= firstInstAmt);
  const step4Valid = installments.length > 0 && sumValid && advanceValid;

  // Phase 4.3 C11 — champagne particle burst on Create
  const [celebrate, setCelebrate] = useState(false);
  const triggerCelebration = () => {
    setCelebrate(true);
    setTimeout(() => setCelebrate(false), 650);
  };

  const goNext = () => {
    if (step === 1 && !step1Valid) { toast.error("Primary name and phone are required"); return; }
    if (step === 2 && !step2Valid) { toast.error("Wedding date and total quote are required"); return; }
    if (step === 4 && !step4Valid) {
      if (!sumValid) toast.error("Installment amounts must sum to total quote");
      else if (advanceAmt <= 0) toast.error("Advance amount is required");
      else if (firstInstAmt > 0 && advanceAmt > firstInstAmt) toast.error("Advance cannot exceed first installment");
      return;
    }
    setStep(step + 1);
  };

  const submit = async () => {
    if (!step1Valid || !step2Valid || !step4Valid) return;
    setSubmitting(true);
    triggerCelebration();
    try {
      const body = {
        primary_name: primaryName.trim(),
        partner_name: partnerName.trim() || null,
        phone: phone.trim(),
        email: (email || "").trim() || null,
        secondary_contacts: secondaryContacts
          .filter((c) => (c.name || "").trim() || (c.phone || "").trim())
          .map((c) => ({ name: c.name || null, relation: c.relation || null, phone: c.phone || null, email: c.email || null })),
        title: title.trim() || null,
        wedding_date: weddingDate,
        wedding_venue: weddingVenue.trim() || null,
        total_quote_value: parseInt(totalQuote || 0, 10),
        events: events.filter((e) => (e.type || "").trim()).map((e) => ({
          id: e.id || undefined,  // pass through stable event_id when known
          type: e.type.trim(), date: e.date || null, venue: e.venue || null, services: e.services || [],
        })),
        payment_schedule_draft: {
          type: scheduleType,
          installments: installments.map((i) => ({
            label: i.label || "",
            amount: parseInt(i.amount || 0, 10),
            due_at: i.due_at || null,
            due_at_source: i.due_at_source || "wedding_relative",
          })),
        },
        advance_payment: {
          amount: parseInt(advanceAmount || 0, 10),
          mode: advanceMode,
          received_at: advanceDate,
          reference: (advanceRef || "").trim() || null,
        },
        // Phase 5 Wizard Deliverable Quantities patch — Step 3 captures.
        wizard_deliverable_quantities: Object.entries(quantitiesByKey).map(([k, q]) => {
          const [service_type, deliverable_type] = k.split("::");
          return { service_type, deliverable_type, quantity: (q === "" ? null : q) };
        }),
        sales_notes: (salesNotes || "").trim() || null,
      };
      const { data } = await api.post(`/leads/${lead.lead_id}/onboard`, body);
      toast.success(
        isEdit ? "Project updated" : "Project created",
        { description: <a className="underline" href={`/projects/${data.project.id}`} data-testid="onboard-toast-link">View project →</a> }
      );
      onComplete?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(14,48,48,0.45)" }} onClick={safeClose} data-testid="onboarding-wizard" data-celebrate={celebrate ? "true" : "false"}>
      <div className="bg-white w-full max-w-[720px] max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-7 pt-7 pb-5 border-b border-brand-border">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="kicker mb-2">{isEdit ? "EDIT PROJECT" : "ONBOARD"} · STEP {step} OF 5</div>
              <h2 className="font-display text-3xl" style={{ color: "var(--brand-ink)" }}>
                {step === 1 && "Client"}
                {step === 2 && "Project"}
                {step === 3 && "Deliverables"}
                {step === 4 && "Payment schedule"}
                {step === 5 && "Review & create"}
              </h2>
            </div>
            <button onClick={safeClose} data-testid="onboard-close"><X size={18} /></button>
          </div>
          {/* Step indicator */}
          <div className="flex gap-1.5 mt-5" data-testid="step-indicator">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="flex-1 h-1" data-active={s <= step ? "true" : "false"} />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="px-7 py-6" data-testid={`wizard-step-${step}`}>
          {step === 1 && (
            <div className="space-y-4 text-sm">
              <Field label="Primary name" required>
                <input value={primaryName} onChange={(e) => setPrimaryName(e.target.value)}
                       className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                       data-testid="wiz-primary-name" />
              </Field>
              <Field label="Partner name">
                <input value={partnerName} onChange={(e) => setPartnerName(e.target.value)}
                       className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                       placeholder="e.g., Sravya"
                       data-testid="wiz-partner-name" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" required>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)}
                         className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                         data-testid="wiz-phone" />
                </Field>
                <Field label="Email">
                  <input value={email} onChange={(e) => setEmail(e.target.value)}
                         className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                         data-testid="wiz-email" />
                </Field>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="kicker">SECONDARY CONTACTS (UP TO 5)</div>
                  {secondaryContacts.length < 5 && (
                    <button onClick={addContact}
                            className="text-xs underline" data-testid="wiz-add-contact">
                      + Add contact
                    </button>
                  )}
                </div>
                {secondaryContacts.length === 0 && (
                  <p className="text-xs text-brand-muted italic">No secondary contacts yet.</p>
                )}
                <div className="space-y-3">
                  {secondaryContacts.map((c, i) => (
                    <div key={i} className="bg-brand-paper border border-brand-border p-3" data-testid={`wiz-contact-${i}`}>
                      <div className="grid grid-cols-2 gap-2 mb-2">
                        <input placeholder="Name" value={c.name || ""} onChange={(e) => setContact(i, { name: e.target.value })} className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }} data-testid={`wiz-contact-name-${i}`} />
                        <input placeholder="Relation (e.g., Father, Planner)" value={c.relation || ""} onChange={(e) => setContact(i, { relation: e.target.value })} className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }} data-testid={`wiz-contact-rel-${i}`} />
                      </div>
                      <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                        <input placeholder="Phone" value={c.phone || ""} onChange={(e) => setContact(i, { phone: e.target.value })} className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }} data-testid={`wiz-contact-phone-${i}`} />
                        <input placeholder="Email" value={c.email || ""} onChange={(e) => setContact(i, { email: e.target.value })} className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }} data-testid={`wiz-contact-email-${i}`} />
                        <button onClick={() => removeContact(i)} className="text-brand-muted" data-testid={`wiz-contact-remove-${i}`}><Trash2 size={14} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4 text-sm">
              <Field label="Project title">
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                       className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                       data-testid="wiz-title" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Wedding date" required>
                  <input type="date" value={weddingDate} onChange={(e) => setWeddingDate(e.target.value)}
                         className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                         data-testid="wiz-wedding-date" />
                </Field>
                <Field label="Wedding venue">
                  <input value={weddingVenue} onChange={(e) => setWeddingVenue(e.target.value)}
                         className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                         data-testid="wiz-wedding-venue" />
                </Field>
              </div>
              <Field label="Total quote (₹)" required>
                <input type="number" value={totalQuote} onChange={(e) => setTotalQuote(e.target.value)}
                       className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                       data-testid="wiz-total-quote" />
              </Field>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="kicker">EVENTS</div>
                  <button onClick={addEvent} className="text-xs underline" data-testid="wiz-add-event">+ Add event</button>
                </div>
                <div className="space-y-3" data-testid="wiz-events-list">
                  {events.map((ev, i) => (
                    <EventEditor
                      key={i}
                      idx={i}
                      ev={ev}
                      eventTypes={eventTypes}
                      serviceTypes={serviceTypes}
                      onChange={(patch) => setEvent(i, patch)}
                      onToggleService={(s) => toggleEventService(i, s)}
                      onRemove={() => removeEvent(i)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (() => {
            // Phase 5 Wizard Deliverable Quantities patch — collect editable
            // services across all events on this project (deduplicated).
            const presentServices = new Set();
            for (const ev of events || []) {
              for (const st of ev.services || []) {
                if (["Candid Photo", "Candid Video", "Traditional Photo", "Traditional Video"].includes(st)) {
                  presentServices.add(st);
                }
              }
            }
            const ordered = ["Candid Photo", "Candid Video", "Traditional Photo", "Traditional Video"]
              .filter((s) => presentServices.has(s));
            if (ordered.length === 0) {
              return (
                <div className="space-y-4 text-sm" data-testid="wiz-deliverables-empty">
                  <p className="text-brand-muted italic">No editing-flow services on this project. Click Next to skip.</p>
                </div>
              );
            }
            return (
              <div className="space-y-6 text-sm" data-testid="wiz-deliverables">
                <p className="text-xs text-brand-muted italic">
                  Pre-filled from your studio&apos;s starter quantities. Adjust to match what you&apos;ve quoted.
                </p>
                {ordered.map((stype) => (
                  <div key={stype} className="border border-brand-border bg-brand-paper px-4 py-3"
                       data-testid={`wiz-deliv-svc-${stype.replace(/\s+/g, "-").toLowerCase()}`}>
                    <div className="font-display text-base mb-3" style={{ color: "var(--brand-ink)" }}>
                      {stype}
                    </div>
                    <div className="space-y-2">
                      {(catalogDefaults[stype] || []).map((row) => {
                        const k = `${stype}::${row.deliverable_type}`;
                        const v = quantitiesByKey[k];
                        const value = v === undefined ? row.quantity : v;
                        return (
                          <div key={k} className="grid grid-cols-[1.4fr_0.6fr_1fr] gap-3 items-center">
                            <div>{row.deliverable_type}</div>
                            <input type="number"
                                   value={value === null ? "" : value}
                                   onChange={(e) => {
                                     const raw = e.target.value;
                                     setQuantitiesByKey((m) => ({
                                       ...m,
                                       [k]: raw === "" ? null : parseInt(raw, 10),
                                     }));
                                   }}
                                   className="px-2 py-1 border border-brand-border bg-white text-right"
                                   style={{ borderRadius: 0 }}
                                   data-testid={`wiz-deliv-qty-${k.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
                            <div className="text-xs text-brand-muted">
                              {row.unit} · due {row.due_days_from_shoot} days from shoot
                            </div>
                          </div>
                        );
                      })}
                      {(catalogDefaults[stype] || []).length === 0 && (
                        <p className="text-xs italic text-brand-muted">No catalog starters defined for {stype}.</p>
                      )}
                    </div>
                  </div>
                ))}
                <Field label="Notes for delivery team (optional)">
                  <textarea
                    value={salesNotes}
                    onChange={(e) => setSalesNotes(e.target.value.slice(0, 500))}
                    placeholder="e.g. Discussed 800 photos but client wants to see samples first."
                    rows={3}
                    maxLength={500}
                    className="w-full px-3 py-2 border border-brand-border bg-white text-sm"
                    style={{ borderRadius: 0 }}
                    data-testid="wiz-sales-notes"
                  />
                  <div className="text-[10px] text-brand-muted mt-1">{salesNotes.length} / 500</div>
                </Field>
              </div>
            );
          })()}

          {step === 4 && (
            <div className="space-y-5 text-sm">
              <div>
                <div className="kicker mb-3">SCHEDULE TYPE</div>
                <div className="grid grid-cols-2 gap-2" data-testid="wiz-schedule-type">
                  {SCHEDULE_TYPES.map((t) => (
                    <button
                      key={t.key}
                      onClick={() => setScheduleType(t.key)}
                      className="px-3 py-3 text-sm border text-left"
                      style={{
                        background: scheduleType === t.key ? "var(--brand-primary)" : "transparent",
                        color: scheduleType === t.key ? "#fff" : "var(--brand-ink)",
                        borderColor: scheduleType === t.key ? "var(--brand-primary)" : "var(--brand-border-soft)",
                      }}
                      data-testid={`wiz-schedule-${t.key}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="kicker">INSTALLMENTS</div>
                  {scheduleType === "custom" && (
                    <button onClick={addInstallment} className="text-xs underline" data-testid="wiz-add-installment">+ Add installment</button>
                  )}
                </div>
                <div className="space-y-2" data-testid="wiz-installments-list">
                  {installments.map((inst, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`wiz-installment-${i}`}>
                      {scheduleType === "custom" ? (
                        <input value={inst.label || ""} onChange={(e) => setInstallment(i, { label: e.target.value })}
                               placeholder="Label" className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }} />
                      ) : (
                        <div className="text-sm">{inst.label}</div>
                      )}
                      <input type="number" value={inst.amount || 0} onChange={(e) => setInstallment(i, { amount: parseInt(e.target.value || "0", 10) })}
                             disabled={scheduleType !== "custom"}
                             className="px-2 py-1.5 text-sm border border-brand-border bg-white text-right disabled:bg-brand-paper" style={{ borderRadius: 0 }}
                             data-testid={`wiz-installment-amount-${i}`} />
                      <div className="flex items-center gap-2">
                        <input type="date" value={inst.due_at || ""} onChange={(e) => setInstallment(i, { due_at: e.target.value })}
                               className="px-2 py-1.5 text-sm border border-brand-border bg-white flex-1" style={{ borderRadius: 0 }}
                               data-testid={`wiz-installment-date-${i}`} />
                        {inst.due_at_source === "fixed" && (
                          <span className="px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase" style={{ background: "var(--brand-champagne)", color: "#fff" }} data-testid={`wiz-installment-edited-${i}`}>EDITED</span>
                        )}
                      </div>
                      {scheduleType === "custom" ? (
                        <button onClick={() => removeInstallment(i)} className="text-brand-muted"><Trash2 size={14} /></button>
                      ) : <div></div>}
                    </div>
                  ))}
                </div>

                {!sumValid && (
                  <div className="mt-3 px-3 py-2 text-xs" style={{ background: "var(--brand-danger-bg)", color: "var(--brand-danger)" }} data-testid="wiz-sum-error">
                    Installments sum {fmtINR(installmentsSum)} · Quote {fmtINR(parseInt(totalQuote || 0, 10))} · Difference {fmtINR((parseInt(totalQuote || 0, 10)) - installmentsSum)}
                  </div>
                )}
                {sumValid && installments.length > 0 && (
                  <div className="mt-3 text-xs text-brand-muted" data-testid="wiz-sum-ok">Total: {fmtINR(installmentsSum)} ✓</div>
                )}
              </div>

              <div className="text-xs text-brand-muted italic">
                Draft schedule — payment tracking comes in the Accounts redesign phase.
              </div>

              {/* Phase 4.2 — Advance received */}
              <div className="pt-5 border-t border-brand-border" data-testid="wiz-advance-section">
                <div className="kicker mb-3">ADVANCE RECEIVED</div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Amount (₹)" required>
                    <input type="number" value={advanceAmount}
                           onChange={(e) => { setAdvanceUserEdited(true); setAdvanceAmount(parseInt(e.target.value || "0", 10)); }}
                           className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                           data-testid="wiz-advance-amount" />
                  </Field>
                  <Field label="Payment method">
                    <select value={advanceMode} onChange={(e) => setAdvanceMode(e.target.value)}
                            className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                            data-testid="wiz-advance-mode">
                      <option value="upi">UPI</option>
                      <option value="bank_transfer">Bank transfer</option>
                      <option value="cash">Cash</option>
                      <option value="cheque">Cheque</option>
                      <option value="card">Card</option>
                    </select>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <Field label="Date received">
                    <input type="date" value={advanceDate} onChange={(e) => setAdvanceDate(e.target.value)}
                           className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                           data-testid="wiz-advance-date" />
                  </Field>
                  <Field label="Reference">
                    <input value={advanceRef} onChange={(e) => setAdvanceRef(e.target.value)}
                           className="w-full px-3 py-2 border border-brand-border bg-white" style={{ borderRadius: 0 }}
                           placeholder="UPI ref / cheque #" data-testid="wiz-advance-ref" />
                  </Field>
                </div>
                {(() => {
                  const amt = parseInt(advanceAmount || 0, 10) || 0;
                  const firstAmt = parseInt(installments[0]?.amount || 0, 10) || 0;
                  if (amt <= 0) {
                    return <div className="mt-3 px-3 py-2 text-xs" style={{ background: "var(--brand-danger-bg)", color: "var(--brand-danger)" }} data-testid="wiz-advance-error">Advance amount must be greater than zero.</div>;
                  }
                  if (firstAmt > 0 && amt > firstAmt) {
                    return <div className="mt-3 px-3 py-2 text-xs" style={{ background: "var(--brand-danger-bg)", color: "var(--brand-danger)" }} data-testid="wiz-advance-error">Advance cannot exceed the first installment ({fmtINR(firstAmt)}).</div>;
                  }
                  const total = parseInt(totalQuote || 0, 10) || 0;
                  const balance = Math.max(0, total - amt);
                  const remainingInstallments = installments.length > 1 ? installments.length - 1 + (amt < firstAmt ? 1 : 0) : (amt < total ? 1 : 0);
                  const isPartial = amt < firstAmt && firstAmt > 0;
                  return (
                    <div className="mt-3 px-3 py-2 text-xs text-brand-muted" data-testid="wiz-advance-balance">
                      {isPartial && <><span className="px-1.5 py-0.5 text-[9px] tracking-[0.18em] uppercase mr-2" style={{ background: "var(--brand-champagne)", color: "#fff" }}>PARTIAL</span></>}
                      Balance after advance: {fmtINR(balance)} · {remainingInstallments} installment{remainingInstallments === 1 ? "" : "s"} remaining
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {step === 5 && (
            <ReviewStep
              primaryName={primaryName} partnerName={partnerName} phone={phone} email={email}
              secondaryContacts={secondaryContacts}
              title={title} weddingDate={weddingDate} weddingVenue={weddingVenue} totalQuote={totalQuote}
              events={events} scheduleType={scheduleType} installments={installments}
              advanceAmount={advanceAmount} advanceMode={advanceMode} advanceDate={advanceDate} advanceRef={advanceRef}
              quantitiesByKey={quantitiesByKey} salesNotes={salesNotes}
              onJump={setStep}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-7 py-5 border-t border-brand-border flex items-center justify-between bg-brand-paper">
          <button onClick={safeClose} className="text-sm underline text-brand-muted" data-testid="wiz-cancel">Cancel</button>
          <div className="flex gap-3">
            {step > 1 && (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm border border-brand-border flex items-center gap-1" data-testid="wiz-back">
                <ArrowLeft size={12} /> Back
              </button>
            )}
            {step < 5 && (
              <button onClick={goNext} className="px-5 py-2 text-sm font-medium text-white flex items-center gap-1" style={{ background: "var(--brand-primary)" }} data-testid="wiz-next">
                Next <ArrowRight size={12} />
              </button>
            )}
            {step === 5 && (
              <button onClick={submit} disabled={submitting} className="px-5 py-2 text-sm font-medium text-white" style={{ background: "var(--brand-champagne)" }} data-testid="wiz-create">
                {submitting ? "Creating…" : (isEdit ? "Save project" : "Create project")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="kicker mb-1.5">{label} {required && <span style={{ color: "var(--brand-danger)" }}>*</span>}</div>
      {children}
    </label>
  );
}

function EventEditor({ idx, ev, eventTypes, serviceTypes, onChange, onToggleService, onRemove }) {
  const [customMode, setCustomMode] = useState(ev.type && !eventTypes.includes(ev.type));
  return (
    <div className="bg-brand-paper border border-brand-border p-3" data-testid={`wiz-event-${idx}`}>
      <div className="grid grid-cols-[1.4fr_1fr_1.4fr_auto] gap-2 mb-2 items-center">
        {customMode ? (
          <input value={ev.type} onChange={(e) => onChange({ type: e.target.value })} placeholder="Custom event"
                 className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }}
                 data-testid={`wiz-event-${idx}-type-custom`} />
        ) : (
          <select value={ev.type} onChange={(e) => { if (e.target.value === "__custom__") { setCustomMode(true); onChange({ type: "" }); } else onChange({ type: e.target.value }); }}
                  className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }}
                  data-testid={`wiz-event-${idx}-type`}>
            <option value="">Pick…</option>
            {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            <option value="__custom__">Custom…</option>
          </select>
        )}
        <input type="date" value={ev.date || ""} onChange={(e) => onChange({ date: e.target.value })}
               className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }}
               data-testid={`wiz-event-${idx}-date`} />
        <input value={ev.venue || ""} onChange={(e) => onChange({ venue: e.target.value })} placeholder="Venue"
               className="px-2 py-1.5 text-sm border border-brand-border bg-white" style={{ borderRadius: 0 }}
               data-testid={`wiz-event-${idx}-venue`} />
        <button onClick={onRemove} className="text-brand-muted" data-testid={`wiz-event-${idx}-remove`}><Trash2 size={14} /></button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {serviceTypes.map((s) => (
          <button
            key={s}
            onClick={() => onToggleService(s)}
            type="button"
            className="px-2.5 py-1 text-[11px] border"
            style={{
              background: ev.services.includes(s) ? "var(--brand-primary)" : "transparent",
              color: ev.services.includes(s) ? "#fff" : "var(--brand-ink)",
              borderColor: ev.services.includes(s) ? "var(--brand-primary)" : "var(--brand-border-soft)",
              borderRadius: 9999,
            }}
            data-testid={`wiz-event-${idx}-svc-${s}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewStep({ primaryName, partnerName, phone, email, secondaryContacts, title, weddingDate, weddingVenue, totalQuote, events, scheduleType, installments, advanceAmount, advanceMode, advanceDate, advanceRef, quantitiesByKey, salesNotes, onJump }) {
  const modeLabel = { upi: "UPI", bank_transfer: "Bank transfer", cash: "Cash", cheque: "Cheque", card: "Card" }[advanceMode] || advanceMode;
  return (
    <div className="space-y-5 text-sm" data-testid="wiz-review">
      <ReviewSection title="Client" onEdit={() => onJump(1)}>
        <div>{primaryName}{partnerName ? ` & ${partnerName}` : ""}</div>
        <div className="text-brand-muted text-xs mt-1">{phone}{email && ` · ${email}`}</div>
        {secondaryContacts.filter((c) => c.name || c.phone).length > 0 && (
          <div className="mt-2">
            <div className="kicker mb-1">SECONDARY</div>
            <ul className="text-xs">
              {secondaryContacts.filter((c) => c.name || c.phone).map((c, i) => (
                <li key={i}>· {c.name || "—"}{c.relation && ` · ${c.relation}`}{c.phone && ` · ${c.phone}`}</li>
              ))}
            </ul>
          </div>
        )}
      </ReviewSection>

      <ReviewSection title="Project" onEdit={() => onJump(2)}>
        <div>{title || <span className="italic text-brand-muted">no title</span>}</div>
        <div className="text-brand-muted text-xs mt-1">{weddingDate || "no date"} · {weddingVenue || "venue tbd"} · {fmtINR(totalQuote || 0)}</div>
        <div className="mt-2 space-y-1">
          {events.filter((e) => e.type).map((e, i) => (
            <div key={i} className="text-xs">· {e.type} {e.date && `· ${e.date}`} {e.services?.length > 0 && `· ${e.services.join(", ")}`}</div>
          ))}
        </div>
      </ReviewSection>

      <ReviewSection title="Deliverables" onEdit={() => onJump(3)}>
        {(() => {
          const presentServices = new Set();
          for (const ev of events || []) {
            for (const st of ev.services || []) {
              if (["Candid Photo", "Candid Video", "Traditional Photo", "Traditional Video"].includes(st)) {
                presentServices.add(st);
              }
            }
          }
          const editable = [...presentServices];
          const rowsByService = {};
          for (const r of quantitiesByKey ? [] : []) void r;  // satisfy lint
          for (const [k, q] of Object.entries(quantitiesByKey || {})) {
            const [svc, dtype] = k.split("::");
            if (!rowsByService[svc]) rowsByService[svc] = [];
            rowsByService[svc].push({ dtype, q });
          }
          if (editable.length === 0) {
            return <div className="text-xs italic text-brand-muted" data-testid="wiz-review-deliverables-empty">No editing-flow services on this project.</div>;
          }
          return (
            <div className="space-y-2 text-xs" data-testid="wiz-review-deliverables">
              {editable.map((svc) => (
                <div key={svc}>
                  <div className="font-medium" style={{ color: "var(--brand-ink)" }}>{svc}</div>
                  {(rowsByService[svc] || []).length === 0 ? (
                    <div className="text-brand-muted italic">Using catalog starters</div>
                  ) : (
                    <ul className="ml-3">
                      {rowsByService[svc].map((row) => (
                        <li key={row.dtype} data-testid={`wiz-review-deliv-${svc.replace(/\s+/g, "-").toLowerCase()}-${row.dtype.replace(/\s+/g, "-").toLowerCase()}`}>
                          · {row.dtype}: {row.q === null || row.q === "" ? <span className="italic">catalog default</span> : row.q}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
              {salesNotes && salesNotes.trim() && (
                <div className="mt-3 pt-2 border-t border-brand-border" data-testid="wiz-review-sales-notes">
                  <div className="kicker mb-1">NOTES FOR DELIVERY</div>
                  <div className="text-brand-muted whitespace-pre-wrap">{salesNotes}</div>
                </div>
              )}
            </div>
          );
        })()}
      </ReviewSection>

      <ReviewSection title="Payment schedule" onEdit={() => onJump(4)}>
        <div className="text-xs text-brand-muted uppercase tracking-[0.18em] mb-2">{SCHEDULE_TYPES.find((t) => t.key === scheduleType)?.label}</div>
        <div className="space-y-1 text-xs">
          {installments.map((i, idx) => (
            <div key={idx} className="flex justify-between">
              <span>{i.label || `Installment ${idx + 1}`} {i.due_at && `· ${i.due_at}`} {i.due_at_source === "fixed" && <span className="ml-1 px-1 text-[8px] tracking-[0.18em] uppercase" style={{ background: "var(--brand-champagne)", color: "#fff" }}>EDITED</span>}</span>
              <span>{fmtINR(i.amount || 0)}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-brand-border" data-testid="wiz-review-advance">
          <div className="kicker mb-1">ADVANCE RECEIVED</div>
          <div className="flex justify-between text-xs">
            <span>{fmtINR(advanceAmount || 0)} via {modeLabel}{advanceDate && ` on ${advanceDate}`}{advanceRef && ` · ${advanceRef}`}</span>
          </div>
        </div>
      </ReviewSection>
    </div>
  );
}

function ReviewSection({ title, onEdit, children }) {
  return (
    <div className="border border-brand-border p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="kicker">{title.toUpperCase()}</div>
        <button onClick={onEdit} className="text-xs underline text-brand-muted flex items-center gap-1"><Edit3 size={11} /> Edit</button>
      </div>
      {children}
    </div>
  );
}
